/**
 * Help System Content
 * Organized by feature/section for easy maintenance and localization
 */

export interface HelpArticle {
  id: string;
  title: string;
  category: string;
  summary: string;
  content: string;
  related?: string[];
}

export interface HelpCategory {
  id: string;
  title: string;
  icon: string;
  description: string;
  articles: string[];
}

export const helpCategories: HelpCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'Rocket',
    description: 'Learn the basics of the inventory system',
    articles: ['overview', 'first-login', 'navigation', 'user-roles']
  },
  {
    id: 'supplies',
    title: 'Supply Management',
    icon: 'Package',
    description: 'Managing your raw materials and supplies',
    articles: ['supplies-overview', 'supply-types', 'supply-categories', 'adding-supplies', 'sku-guide', 'stock-tracking']
  },
  {
    id: 'products',
    title: 'Products & Variants',
    icon: 'Box',
    description: 'Creating and managing sellable products',
    articles: ['products-overview', 'product-type-vs-category', 'creating-products', 'product-variants', 'bom-setup', 'production']
  },
  {
    id: 'inventory',
    title: 'Inventory & Locations',
    icon: 'MapPin',
    description: 'Tracking stock across locations',
    articles: ['inventory-overview', 'storage-locations', 'shelf-inventory', 'stock-transfers']
  },
  {
    id: 'sales',
    title: 'Sales & Commission',
    icon: 'DollarSign',
    description: 'Recording sales and managing commissions',
    articles: ['recording-sales', 'commission-tracking', 'location-payments']
  },
  {
    id: 'settings',
    title: 'Settings & Admin',
    icon: 'Settings',
    description: 'System configuration and user management',
    articles: ['user-management', 'reference-data', 'email-settings']
  }
];

export const helpArticles: Record<string, HelpArticle> = {
  // ==================== GETTING STARTED ====================
  'overview': {
    id: 'overview',
    title: 'System Overview',
    category: 'getting-started',
    summary: 'Understanding the Fun 1st Treasures Inventory System',
    content: `
## What is This System?

The Fun 1st Treasures Inventory System helps you manage:

1. **Supplies** - Raw materials like filament, hardware, packaging
2. **Products** - Items you sell (with variants and configurations)
3. **Inventory** - Stock tracking across multiple locations
4. **Sales** - Recording sales and tracking commissions

## Key Concepts

### Supply vs Product
- **Supplies** are things you BUY and USE to make products
- **Products** are things you SELL to customers

### Locations vs Storage
- **Locations** are where you sell (booths, online, etc.)
- **Storage Locations** are where you keep inventory (rooms, shelves, bins)

### Bill of Materials (BOM)
A product's "recipe" - lists all supplies needed to make one unit.

## Getting Help

- Look for the ⓘ icon next to fields for quick tips
- Click "Help" in the navigation for full documentation
- Use the search to find specific topics
    `.trim()
  },

  'first-login': {
    id: 'first-login',
    title: 'First Time Login',
    category: 'getting-started',
    summary: 'What to do when you first access the system',
    content: `
## Welcome! Here's Your First Steps:

### 1. Check Your User Role
Your account has one of these roles:
- **Admin** - Full access to everything
- **Manager** - Can manage inventory and products
- **Viewer** - Can view reports only

### 2. Set Up Reference Data (Admins)
Before adding supplies, configure:
- **Suppliers** - Where you buy from (Amazon, Hatchbox, etc.)
- **Colors** - For organizing filament
- **Brands** - For manufacturer tracking

Go to: Settings → Reference Data

### 3. Add Your First Supply
Start with something simple:
- Go to Supplies → Add Supply
- Choose a Supply Type (Filament, Hardware, etc.)
- Fill in name, SKU, and stock quantity

### 4. Create Your First Product
- Go to Products → Add Product
- Set the product type (3D Printed, Vinyl, etc.)
- Add variants (colors, sizes)
- Set up the Bill of Materials

### 5. Record Your First Sale
- Go to Sales → Record Sale
- Select the product variant
- Choose the location
- Enter quantity and price
    `.trim()
  },

  'user-roles': {
    id: 'user-roles',
    title: 'Understanding User Roles',
    category: 'getting-started',
    summary: 'What each user role can and cannot do',
    content: `
## User Roles & Permissions

### Admin
**Full system access.**

Can do everything including:
- Add/remove users
- Change user roles
- Access all settings
- Delete anything

Best for: Business owners, system administrators

### Manager
**Day-to-day operations.**

Can:
- Add/edit supplies and products
- Record sales and transfers
- View all reports
- Manage inventory

Cannot:
- Add/remove users
- Change user roles
- Access system settings

Best for: Shop managers, production leads

### Viewer
**Read-only access.**

Can:
- View supplies and products
- View inventory levels
- View sales reports
- View audit logs

Cannot:
- Create or edit anything
- Delete anything
- Record sales or transfers

Best for: Investors, accountants, observers

## Changing Roles
Only Admins can change user roles:
1. Go to Settings → Users
2. Find the user
3. Click the Role dropdown
4. Select new role
    `.trim()
  },

  // ==================== SUPPLIES ====================
  'supplies-overview': {
    id: 'supplies-overview',
    title: 'Supply Management Overview',
    category: 'supplies',
    summary: 'Understanding how supplies work in the system',
    content: `
## What Are Supplies?

Supplies are raw materials and components you purchase to create your products.

### Examples of Supplies:
- 3D printer filament (PLA, PETG, TPU)
- Keychain hardware (rings, clasps, magnets)
- Packaging materials (bags, boxes, labels)
- Electronics (LEDs, batteries, wiring)
- Fasteners (glue, tape, screws)

## Supply Tracking

The system tracks:
- **Quantity** - How much you have in stock
- **Cost** - What you paid per unit
- **Supplier** - Where you bought it from
- **Location** - Where it's stored
- **SKU** - Your internal part number

## Supply Types vs Categories

We organize supplies in TWO ways:

### 1. Supply Type (Required)
**What the item physically IS.**

This determines how the system tracks the supply:
- **Filament** → Tracked by weight (grams/kg)
- **Hardware** → Tracked by count (pieces)
- **Packaging** → Tracked by count (pieces/boxes)

### 2. Business Category (Optional)
**How YOU organize for business.**

This helps with reporting and budgeting:
- **3D Printing** - All filament, nozzles, cleaning supplies
- **Packaging** - All mailers, boxes, tape, labels
- **Assembly** - All hardware, fasteners, tools

## Example:
| Item | Supply Type | Business Category |
|------|-------------|-------------------|
| Red PLA Filament | Filament | 3D Printing |
| Keychain Rings | Hardware | Assembly |
| Poly Mailers | Packaging | Shipping |
| Isopropyl Alcohol | Raw Material | 3D Printing |
    `.trim()
  },

  'supply-types': {
    id: 'supply-types',
    title: 'Understanding Supply Types',
    category: 'supplies',
    summary: 'The different types of supplies and how they\'re tracked',
    content: `
## Supply Types Explained

The **Supply Type** tells the system what the item physically is and how to track it.

### Available Types:

#### Filament
**For 3D printer filament materials.**
- Tracked by: Weight (grams or kilograms)
- Default unit: Gram
- Examples: PLA, PETG, ABS, TPU, Wood filament

#### Hardware
**Physical components and parts.**
- Tracked by: Count (pieces)
- Default unit: Piece
- Examples: Keychain rings, magnets, clips, hinges

#### Electronic
**Electronic components.**
- Tracked by: Count (pieces)
- Default unit: Piece
- Examples: LEDs, batteries, wiring, switches

#### Packaging
**Materials for shipping products.**
- Tracked by: Count (pieces/boxes)
- Default unit: Piece
- Examples: Mailers, boxes, bubble wrap, tissue paper

#### Accessory
**Decorative add-ons.**
- Tracked by: Count (pieces)
- Default unit: Piece
- Examples: Google eyes, stickers, charms, ribbons

#### Fastener
**Adhesives and connectors.**
- Tracked by: Count or volume
- Default unit: Piece
- Examples: Glue, tape, screws, epoxy

#### Raw Material
**Base materials not in other categories.**
- Tracked by: Various units
- Default unit: Unit
- Examples: Wood sheets, metal stock, chemicals

### How It Affects Tracking:

**Filament Example:**
- You buy: 1kg spool for $25
- Cost per unit: $0.025 per gram
- Use 100g for a product → Cost = $2.50

**Hardware Example:**
- You buy: Pack of 100 rings for $10
- Cost per unit: $0.10 per ring
- Use 1 ring per product → Cost = $0.10
    `.trim()
  },

  'supply-categories': {
    id: 'supply-categories',
    title: 'Business Categories Explained',
    category: 'supplies',
    summary: 'How to use business categories for organization',
    content: `
## Business Categories

**Business Category** is how YOU choose to group supplies for your business operations. Unlike Supply Type (which is physical), this is completely up to you.

### Why Use Categories?

1. **Budgeting** - See spending per category: "I spent $500 on 3D Printing this month"
2. **Reporting** - Group costs by workflow
3. **Organization** - Find supplies faster
4. **Cost Analysis** - "Packaging is 15% of my costs - is that too high?"

### Predefined Categories:

| Category | Typical Supplies |
|----------|------------------|
| **3D Printing** | All filament, nozzles, build plates, cleaning alcohol |
| **Filament** | Just filament (if you want to separate it) |
| **Hardware** | Screws, nuts, bolts, general hardware |
| **Electronics** | LEDs, batteries, wiring, components |
| **Packaging** | Mailers, boxes, tape, labels, thank you cards |
| **Fasteners** | Glue, tape, adhesives specifically |
| **Accessories** | Decorative items, charms, stickers |
| **Resin** | Resin supplies for SLA printing |
| **Laser Supplies** | Acrylic, wood sheets, laser-safe materials |
| **Sublimation** | Sublimation paper, inks, blanks |
| **Tools** | Hand tools, maintenance equipment |
| **Raw Materials** | Bulk materials, uncategorized items |
| **Other** | Everything else |

### Real-World Example:

**Product: Glowing Moon Keychain**

| Supply | Supply Type | Business Category | Why? |
|--------|-------------|-------------------|------|
| White Glow PLA | Filament | 3D Printing | Raw material |
| 0.4mm Nozzle | Hardware | 3D Printing | Printer maintenance |
| Isopropyl Alcohol | Raw Material | 3D Printing | Print cleaning |
| Split Ring | Hardware | Assembly | Product component |
| LED Light | Electronic | Assembly | Product feature |
| Poly Mailer | Packaging | Shipping | Customer delivery |
| Thank You Card | Accessory | Branding | Customer experience |

### Can I Change Categories?

Yes! Categories are flexible:
- Change a supply's category anytime
- Create your own categories (ask admin)
- Use the same category across different supply types

### Pro Tip:

Set up categories to match your **budgeting** or **workflow**:
- By craft type: "3D Printing", "Vinyl Work", "Laser"
- By purpose: "Production", "Packaging", "Marketing"
- By vendor: "Amazon Orders", "Local Store", "Wholesale"
    `.trim()
  },

  'sku-guide': {
    id: 'sku-guide',
    title: 'SKU Guide: Internal, Mfr & Supplier',
    category: 'supplies',
    summary: 'Understanding the three different SKU fields',
    content: `
## Understanding SKUs

We have **three SKU fields** for different purposes:

### 1. Internal SKU (Your Part Number)
**Your own SKU system for tracking.**

- **Purpose**: Your internal part number
- **Should be**: Unique across all supplies
- **Format**: Whatever works for you
- **Examples**: 
  - FIL-RED-001 (Filament, Red, #1)
  - HW-RING-025 (Hardware, Ring, #25)
  - PKG-MAIL-6x9 (Packaging, Mailer, 6x9)

### 2. Manufacturer SKU (Mfr SKU)
**The manufacturer's part number.**

- **Purpose**: Reordering from manufacturer
- **Where to find**: Product packaging, website
- **Examples**:
  - Hatchbox: PLA-RED-1KG
  - Amazon: B08XYZ123
  - Generic: 3D-PLA-175-RED

### 3. Supplier SKU
**The supplier's catalog number.**

- **Purpose**: Reordering from YOUR supplier
- **Where to find**: Your supplier's website, invoice
- **Examples**:
  - Amazon: B08XYZ123 (ASIN)
  - McMaster: 9414K11
  - Fastenal: 12345678

## When to Use Each:

### Scenario 1: Buying from Manufacturer Direct
| Field | Value |
|-------|-------|
| Internal SKU | FIL-PLA-RED |
| Mfr SKU | PLA-RED-1KG |
| Supplier SKU | PLA-RED-1KG |

### Scenario 2: Buying from Amazon
| Field | Value |
|-------|-------|
| Internal SKU | FIL-PLA-RED |
| Mfr SKU | PLA-RED-1KG |
| Supplier SKU | B08XYZ123 (Amazon ASIN) |

### Scenario 3: Generic/Unbranded Item
| Field | Value |
|-------|-------|
| Internal SKU | HW-RING-1IN |
| Mfr SKU | (blank - no manufacturer) |
| Supplier SKU | RING-SPLIT-100PK |

## Pro Tips:

1. **Always set Internal SKU** - This is your main identifier
2. **Set Mfr SKU** if you might order from manufacturer later
3. **Set Supplier SKU** if you reorder from the same place
4. **Add Purchase Link** - Makes reordering one click away

## SKU Naming Convention Ideas:

### Format: [TYPE]-[DETAIL]-[NUMBER]
- FIL-PLA-RED-001
- HW-RING-25MM
- PKG-MAIL-6X9

### Format: [CATEGORY]-[ITEM]-[VARIANT]
- 3DP-FIL-RED
- ASM-RING-GOLD
- SHIP-MAIL-LARGE

Choose what makes sense for YOUR business!
    `.trim()
  },

  'adding-supplies': {
    id: 'adding-supplies',
    title: 'How to Add a New Supply',
    category: 'supplies',
    summary: 'Step-by-step guide to adding supplies',
    content: `
## Adding a New Supply

### Step 1: Go to Supplies Page
Click "Supplies" in the left navigation menu.

### Step 2: Click "Add Supply"
The button is in the top right of the supplies list.

### Step 3: Select Supply Type
Choose what the item physically is:
- **Filament** - For 3D printer filament
- **Hardware** - For physical parts
- **Electronic** - For LEDs, batteries, etc.
- **Packaging** - For shipping materials
- etc.

This affects how stock is tracked!

### Step 4: Fill in Basic Info

**Required:**
- **Name** - Clear, descriptive name (e.g., "Red PLA Filament 1.75mm")

**Optional but Recommended:**
- **Internal SKU** - Your part number
- **Manufacturer SKU** - Manufacturer's part number
- **Category** - How you group for business

### Step 5: Set Inventory Info

- **Quantity in Stock** - How much you have now
- **Unit of Measure** - Pieces, grams, rolls, etc.
- **Cost per Unit** - What you paid (used for product costing)
- **Reorder Level** - When to warn you about low stock

### Step 6: Add Supplier Info

- **Supplier** - Where you bought it (use + button to add new)
- **Supplier SKU** - Their part number
- **Purchase Link** - Direct URL to buy more

### Step 7: Upload Photo (Optional)
Add a photo to help identify the item, especially useful for:
- Filament colors
- Hardware sizes
- Packaging types

### Step 8: Save
Click "Create Supply" and you're done!

## Quick Add for Suppliers

If your supplier isn't in the list:
1. Click the **+** button next to Supplier
2. Enter supplier name
3. Add website URL (optional)
4. Click Add
5. The new supplier is immediately available

## Tips:

- **Be consistent** with naming (e.g., always "Red PLA" not sometimes "PLA Red")
- **Set reorder levels** to avoid running out
- **Add purchase links** for easy reordering
- **Use clear photos** for visual identification
    `.trim()
  },

  // ==================== PRODUCTS ====================
  'products-overview': {
    id: 'products-overview',
    title: 'Product Management Overview',
    category: 'products',
    summary: 'Understanding products and how they relate to supplies',
    related: ['product-type-vs-category', 'creating-products'],
    content: `
## What Are Products?

**Products** are items you SELL to customers. Unlike supplies (which you buy), products are what generate revenue.

### Product vs Supply

| | Supply | Product |
|--|--------|---------|
| **You...** | Buy it | Sell it |
| **Purpose** | Raw material | Finished good |
| **Example** | Red PLA filament | Glowing Moon Keychain |
| **Tracking** | Stock quantity | Production + Sales |

## Product Types

The system supports different product types based on your craft:

### 3D Printed
Products made with a 3D printer
- Examples: Keychains, desk organizers, toys
- Requires: 3D model file, filament selection

### Vinyl Cut
Products made with a vinyl cutter (Cricut, Silhouette)
- Examples: Decals, stickers, iron-on designs
- Requires: Design file, vinyl selection

### Laser Engraved
Products made with a laser engraver
- Examples: Engraved coasters, cutting boards, signs
- Requires: Design file, material selection

### UV Printed
Products made with UV flatbed printer
- Examples: Phone cases, metal prints, acrylic signs
- Requires: Design file, substrate selection

### Apparel
Clothing and fabric items
- Examples: T-shirts, hats, tote bags
- Requires: Design file, apparel blanks

### Standard
Generic products not in other categories
- Examples: Handmade items, assembled goods, purchased inventory

## Product Structure

### Product (Parent)
- Contains: Name, description, base price, category
- Has: Multiple Variants

### Variant (Child)
- Specific configuration of the product
- Examples: Different colors, sizes, or material combinations
- Each variant has its own:
  - SKU
  - Stock level
  - Bill of Materials

## Example Structure:

**Product: "Moon Keychain"**

Variants:
1. **White Glow** - SKU: MK-WHT-001
   - Filament: White glow PLA (15g)
   - Hardware: Split ring (1)
   
2. **Red Standard** - SKU: MK-RED-001
   - Filament: Red PLA (15g)
   - Hardware: Split ring (1)
   
3. **Blue with LED** - SKU: MK-BLU-LED
   - Filament: Blue PLA (15g)
   - Hardware: Split ring (1)
   - Electronic: LED light (1)

## Bill of Materials (BOM)

Each variant has a BOM that lists:
- What supplies are needed
- How much of each
- Total material cost

This calculates:
- Production cost per unit
- How many you can make with current stock
- Profit margins
    `.trim()
  },

  'product-type-vs-category': {
    id: 'product-type-vs-category',
    title: 'Product Type vs Production Method vs Category',
    category: 'products',
    summary: 'Understanding what the customer buys, how it is made, and how it is organized',
    content: `
## Product Type vs Production Method vs Category

These fields work together but serve different purposes:

| Field | Purpose | Examples |
|-------|---------|----------|
| **Product Type** | What the customer **buys** | Keychain, Sign, Magnet, Trophy |
| **Production Method** | How it's **made** | 3D Printed, UV Printed, DTF, Laser Engraved |
| **Design Family (Category)** | How you **organize** designs | Duck, Panda, Seasonal |
| **Collection** | Who it's **for** (optional) | Pride, Bear Gear, Pups |

## Product Type (What They Buy)

Configured under **Catalog → Item Types**. This is Step 1 when adding a product.

## Production Method (How It's Made)

Configured under **Catalog → Production Methods**. A product can have **multiple** methods (e.g. 3D print + UV on top).

The legacy **product_type** enum maps to the primary production method for backward compatibility.

### Common Production Methods:

| Method | Design File | 3D Model |
|--------|-------------|----------|
| **3D Printed** | Optional | Yes (STL/3MF) |
| **UV Printed** | Yes | No |
| **Laser Engraved** | Yes | No |
| **Sublimation / DTF** | Yes | No |
| **Vinyl Cutting** | Yes | No |

## Design Family — Categories

Managed at **Categories** in the sidebar. Use for design families like Duck, Panda, or seasonal groupings.

## Collection / Audience

Optional themed lines under **Catalog → Collections** — Pride, Bear Gear, Valentine's, etc.

## Form Preferences

Set your default product form layout under **Settings → Preferences** or the sliders icon in the top bar:
- **Wizard** — step-by-step guided flow
- **Single Page** — all fields on one scrollable form
    `.trim()
  },

  'bom-setup': {
    id: 'bom-setup',
    title: 'Setting Up Bill of Materials (BOM)',
    category: 'products',
    summary: 'How to configure what supplies each product needs',
    content: `
## What is a Bill of Materials (BOM)?

A BOM is the "recipe" for making a product. It lists every supply needed and how much.

## Why BOMs Matter

1. **Cost Calculation** - Know exactly what each product costs to make
2. **Stock Planning** - See how many you can make with current supplies
3. **Production** - Track supply usage when you make items
4. **Pricing** - Ensure you're charging enough to cover costs

## Setting Up a BOM

### Step 1: Create or Edit a Product Variant
Go to Products → [Your Product] → Variants

### Step 2: Add Supplies to BOM

Click "Add Supply" and select:
- **Supply** - Choose from your supply list
- **Quantity** - How much is needed per unit
- **Unit** - Usually same as supply's unit

### Step 3: Example BOM Setup

**Product: Moon Keychain (White Glow)**

| Supply | Quantity | Unit | Cost Contribution |
|--------|----------|------|-------------------|
| White Glow PLA | 15 | grams | $0.38 |
| Split Ring | 1 | piece | $0.10 |
| Poly Mailer | 1 | piece | $0.25 |
| Thank You Sticker | 1 | piece | $0.05 |
| **TOTAL** | | | **$0.78** |

### Understanding the Numbers

**Cost Contribution** = Supply quantity × Cost per unit

Example:
- White Glow PLA costs $0.025 per gram
- You use 15 grams
- Cost contribution = 15 × $0.025 = $0.38

**Units Makeable** = Shows how many products you can make with current stock

Example:
- You have 500g of White Glow PLA
- Each keychain needs 15g
- Units makeable = 500 ÷ 15 = 33 keychains

## Multi-Supply Products

Complex products can use multiple supply types:

**Example: LED Light-Up Sign**
- Filament (for base): 50g
- LED strip: 1 piece
- Wiring: 0.5 meters
- Acrylic sheet: 1 piece
- Stand hardware: 4 screws

## Tips for Good BOMs

1. **Be precise** - Measure actual usage, not estimates
2. **Include everything** - Even small items like stickers add up
3. **Update when recipes change** - If you start using more filament, update the BOM
4. **Consider waste** - Add 5-10% for failed prints, defects, etc.
5. **Review costs regularly** - Supply prices change, update your BOMs

## Troubleshooting

**"Insufficient Stock" Warning**
- One or more supplies in the BOM are below required quantity
- Check the BOM for red indicators

**Cost Seems Wrong**
- Check supply cost per unit is correct
- Verify quantities in BOM
- Remember: Cost updates when you adjust stock with new pricing
    `.trim()
  },

  // ==================== SALES ====================
  'recording-sales': {
    id: 'recording-sales',
    title: 'Recording a Sale',
    category: 'sales',
    summary: 'How to record sales and track revenue',
    content: `
## Recording a Sale

### Step 1: Go to Sales Page
Click "Sales" in the left navigation.

### Step 2: Click "Record Sale"
Button in the top right.

### Step 3: Fill Sale Details

**Required Fields:**
- **Product Variant** - What was sold
- **Location** - Where it was sold (booth, online, etc.)
- **Quantity** - How many units
- **Sale Price** - Price per unit

**Optional Fields:**
- **Sale Date** - Defaults to today
- **Notes** - Customer info, special circumstances

### Step 4: Save
The system will:
- Record the sale
- Deduct from inventory
- Calculate commission
- Update revenue reports

## Understanding Commission

If the location has a commission rate:
- **Gross Sale**: $100
- **Commission Rate**: 20%
- **Commission**: $20 (goes to location)
- **Your Earnings**: $80

## Viewing Sales History

The Sales page shows:
- All recorded sales
- Filter by date range
- Filter by location
- Filter by product
- Export to CSV

## Tips:

- Record sales immediately after they happen
- Use consistent location names
- Add notes for unusual situations
- Review unpaid commissions regularly
    `.trim()
  },

  // ==================== SETTINGS ====================
  'user-management': {
    id: 'user-management',
    title: 'Managing Users',
    category: 'settings',
    summary: 'How to add, edit, and manage system users',
    content: `
## User Management (Admin Only)

Only users with **Admin** role can manage other users.

### Adding a New User

1. Go to **Settings** → **Users** tab
2. Click **"Add User"** button
3. Fill in the form:
   - **Email** - Their login email
   - **Password** - Temporary password (they can change it)
   - **First Name** - Their first name
   - **Last Name** - Their last name
   - **Role** - Admin, Manager, or Viewer
4. Click **"Create User"**

### User Roles

#### Admin
- Full system access
- Can add/remove users
- Can change all settings
- Can delete anything

#### Manager
- Can manage inventory and products
- Can record sales
- Can view all reports
- **Cannot** manage users or system settings

#### Viewer
- Read-only access
- Can view all data
- **Cannot** create, edit, or delete anything

### Managing Existing Users

**Change Role:**
1. Find the user in the list
2. Click the Role dropdown
3. Select new role
4. Changes take effect immediately

**Activate/Deactivate:**
1. Find the user
2. Click "Deactivate" or "Activate"
3. Deactivated users cannot log in

**Reset Password:**
1. Click "Edit" on the user
2. Check "Reset Password"
3. Enter new password
4. Save

### Best Practices

- Give **Admin** only to trusted people
- Use **Manager** for day-to-day operators
- Use **Viewer** for accountants or investors
- Deactivate (don't delete) former employees
- Use strong passwords
    `.trim()
  },

  'reference-data': {
    id: 'reference-data',
    title: 'Managing Reference Data',
    category: 'settings',
    summary: 'How to manage suppliers, colors, and brands',
    content: `
## Reference Data

Reference data is shared information used across the system:
- **Suppliers** - Where you buy supplies
- **Colors** - For organizing filament and products
- **Brands** - Manufacturer names

### Why Use Reference Data?

1. **Consistency** - Same spelling everywhere
2. **Efficiency** - Quick selection in dropdowns
3. **Reporting** - Group by supplier, brand, etc.
4. **Organization** - Keep your data clean

### Managing Suppliers

**Add a Supplier:**
1. Go to Settings → Reference Data → Suppliers
2. Click "Add Supplier"
3. Enter name (e.g., "Amazon", "Hatchbox")
4. Enter website URL (optional but helpful)
5. Save

**Use in Supply Form:**
- When adding a supply, select from supplier dropdown
- Click "+" button to add new supplier on-the-fly

### Managing Colors

**Add a Color:**
1. Go to Settings → Reference Data → Colors
2. Click "Add Color"
3. Enter name (e.g., "Galaxy Black")
4. Enter hex code (e.g., "#1B1B1B") for visual display
5. Save

**Usage:**
- Organize filament by color
- Color-code products
- Future: Filter by color

### Managing Brands

**Add a Brand:**
1. Go to Settings → Reference Data → Brands
2. Click "Add Brand"
3. Enter manufacturer name
4. Save

**Usage:**
- Track which brands you use most
- Filter supplies by brand
- Compare costs between brands

### Pre-Seeded Data

The system comes with common values pre-loaded:
- 22 suppliers (Amazon, Hatchbox, Bambu Lab, etc.)
- 32 colors (Galaxy Black, Fire Red, etc.)
- 16 brands (Prusa, Polymaker, eSUN, etc.)

You can add, edit, or deactivate any of these.

### Deactivating vs Deleting

We use **soft delete** (deactivation):
- Items are marked inactive
- They no longer appear in dropdowns
- Historical data is preserved
- Can be reactivated later

This prevents breaking old records that reference the item.
    `.trim()
  }
};

// Helper function to search help articles
export function searchHelpArticles(query: string): HelpArticle[] {
  const normalizedQuery = query.toLowerCase().trim();
  
  return Object.values(helpArticles).filter(article => {
    const inTitle = article.title.toLowerCase().includes(normalizedQuery);
    const inSummary = article.summary.toLowerCase().includes(normalizedQuery);
    const inContent = article.content.toLowerCase().includes(normalizedQuery);
    return inTitle || inSummary || inContent;
  });
}

// Helper to get articles by category
export function getArticlesByCategory(categoryId: string): HelpArticle[] {
  const category = helpCategories.find(c => c.id === categoryId);
  if (!category) return [];
  
  return category.articles
    .map(id => helpArticles[id])
    .filter(Boolean);
}

// Helper to get related articles
export function getRelatedArticles(articleId: string): HelpArticle[] {
  const article = helpArticles[articleId];
  if (!article?.related) return [];
  
  return article.related
    .map(id => helpArticles[id])
    .filter(Boolean);
}
